import _ from 'lodash'

import {client} from '@/utils/gql'
import {
  User_Bool_Exp,
  User_Insert_Input,
  User_Order_By,
  User_Set_Input,
  UserFragment,
} from '@/utils/graphqlSdk'
import {NotFoundError, ServerError} from '@/utils/server/errors'

const getByIdOrNull = async (id: string): Promise<UserFragment | null> =>
  (await client.userGetById({id})).user_by_pk || null

const getById = async (id: string): Promise<UserFragment> => {
  const user = await getByIdOrNull(id)

  if (!user) {
    throw new NotFoundError('User does not exist', {id})
  }

  return user
}

const getOneOrNull = async (filter: User_Bool_Exp): Promise<UserFragment | null> => {
  const user = (await client.userGetMany({filter})).user

  if (user.length > 1) {
    throw new ServerError('Get one returned multiple results', {filter})
  }

  return user?.[0] ?? null
}

const getOne = async (filter: User_Bool_Exp): Promise<UserFragment> => {
  const user = await getOneOrNull(filter)

  if (!user) {
    throw new NotFoundError('Get one returned null', {filter})
  }

  return user
}

const getMany = async (
  filter: User_Bool_Exp,
  order_by?: User_Order_By,
  limit?: number,
): Promise<UserFragment[]> =>
  (
    await client.userGetMany({
      filter,
      order_by,
      limit,
    })
  ).user

const createOne = async (object: User_Insert_Input): Promise<UserFragment> => {
  const result = (await client.userInsertOne({object})).insert_user_one

  if (!result) {
    throw new ServerError('User not returned after insert', {object})
  }

  return result
}

const updateOne = async (
  id: string,
  changes: User_Set_Input,
  config?: {allowEmailChange: boolean},
): Promise<UserFragment> => {
  const {allowEmailChange = false} = config ?? {}

  const postProcessedChanges = allowEmailChange ? changes : _.omit(changes, ['email'])

  const result = (await client.userUpdateOne({id, changes: postProcessedChanges}))
    ?.update_user_by_pk

  if (!result) {
    throw new ServerError('User not returned after update', {id, changes})
  }

  return result
}

export const userOrm = {
  getById,
  getByIdOrNull,
  getOneOrNull,
  getOne,
  getMany,
  createOne,
  updateOne,
}
